import React, { FunctionComponent, PropsWithChildren, useState } from 'react'
import { useCombobox } from 'downshift'
import omit from 'lodash.omit'
import { Box, TiaCoreInputProps, useTiaInput } from '../'
import * as ThemeUI from 'theme-ui'
import { useFormContext } from 'react-hook-form'

export type TypeaheadItem = {
    value: any
    label: string
}

export type TypeaheadProps = {
    /**
     * name of form field that holds the input value
     */
    name: string

    /**
     * initial items to populate typeahead
     */
    items: TypeaheadItem[]

    /**
     * initial value for the field
     */
    initialValue?: string

    /**
     * filter function to search through available items
     * receives current items and search term
     */
    filterItems: (
        inputValue: string,
        items: TypeaheadItem[]
    ) => Promise<TypeaheadItem[]>
}

/**
 * We use Downshift to manage the typehead
 * A hidden input field holds the value that is synced to react-hook-form
 * useTiaInput gives us errors and border color
 * @deprecated use Select instead
 */
export const Typeahead: FunctionComponent<
    React.PropsWithChildren<
        PropsWithChildren<
            TypeaheadProps & ThemeUI.InputProps & TiaCoreInputProps
        >
    >
> = props => {
    const [items, setItems] = useState(props.items)
    const formContext = useFormContext()
    const { error, borderColor, register } = useTiaInput(props)

    const {
        isOpen,
        getInputProps,
        getMenuProps,
        getItemProps,
        getComboboxProps,
        highlightedIndex
    } = useCombobox({
        items,
        onInputValueChange: async ({ inputValue }) => {
            const results = await props.filterItems(inputValue || '', items)

            formContext?.setValue(props.name, inputValue)

            setItems(results)
        },
        itemToString: item => item?.label || '',
        initialInputValue: props.initialValue
    })

    return (
        <Box sx={{ position: 'relative', ...props.sx }} {...getComboboxProps()}>
            <input
                name={props.name}
                ref={register}
                style={{ display: 'none' }}
            />
            <ThemeUI.Input
                {...getInputProps()}
                {...omit(props, ['name'])}
                sx={{ borderColor, mb: 2 }}
                onBlur={() => formContext?.trigger(props.name)}
            />

            <Box sx={{ mt: 5, mb: -16 }}>{error}</Box>

            <Box
                {...getMenuProps()}
                sx={{
                    borderRadius: 2,
                    bg: 'mainBackground',
                    zIndex: 1000,
                    boxShadow: 'inputOutline',
                    position: 'absolute',
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                {isOpen &&
                    items.map((item, index) => (
                        <Box
                            key={`${item.value}${index}`}
                            sx={{
                                p: 4,
                                bg:
                                    highlightedIndex === index
                                        ? 'cardBackground'
                                        : 'mainBackground',
                                cursor: 'pointer'
                            }}
                            {...getItemProps({ item, index })}
                        >
                            {item.label}
                        </Box>
                    ))}
            </Box>
        </Box>
    )
}
